/**
 * CustomerDrawer — view / edit / add one customer. Opened from the Customers screen.
 * Replaces the old Bootstrap modal (candidates/updater/updater.js); logic is ported 1:1.
 *
 * Props (same contract as the old Updater):
 *   flag         'add' | 'update' | 'track'   ('track' = read-only)
 *   info         customer row from the search results (ignored for 'add')
 *   allEmails    { [mail]: "" }   existing emails — duplicate check when adding
 *   allPhones    { [phone]: "" }  existing phones — duplicate check when adding
 *   onClose(saved: bool)          parent re-runs the search when saved === true
 *   statusOptions / tagOptions    Select options from the parent (ui/lookups toOptions)
 *   actions      the customer's actions (WhatsApp, email, call, delete) — same list as the
 *                table row (Customers.js customerActions); shown as a "⋯" menu in the header
 *
 * Sections: פרטים · טאגים · activity switch [היסטוריה | אימיילים]
 *   The old "קישורים" (facebook/instagram links) section was removed from the UI — unused since
 *   2023 (20 customers). `mediaLinks` stays in the form state, so saving keeps existing links.
 *   (the אימיילים tab = mailing settings + consent, right above the sent-emails list)
 *
 * Save = insertOrUpdateCustomers(Sform) — the whole form object, including the edited
 * `history` array (entries flagged update/delete), exactly as before.
 *
 * Data loaded on open (unchanged):
 *   GET /api/tags/stats              tag colors + active email sequences (chip details)
 *   getCusHistory(id)                history entries
 *   getCustomerEmailHistory(id)      emails sent to this customer
 */
import React, { useState, useEffect, useRef } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import {
  PlusOutlined,
  DeleteOutlined,
  DownOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleOutlined,
} from "@ant-design/icons";

import * as actionsCreators from "./../../actions/index.js";
import {
  EditDrawer,
  RowActions,
  Section,
  FormGrid,
  Field,
  StatusBadge,
  TagChips,
  EmptyState,
  Button,
  Input,
  Select,
  DatePicker,
  Switch,
  Tooltip,
  Spin,
  Segmented,
  statusColor,
  itemLabel,
} from "../../ui";
import dateToStringForDisplay from "./../../utils/dateToStringForDisplay.js";
import validationEmail from "./../../utils/validationEmail.js";
import styles from "./CustomerDrawer.css";

const infoInit = {
  id: "-1",
  firstname: "",
  lastname: "",
  phone: "",
  mail: "",
  emailSubscribed: true,
  dateEnterSystem: "",
  lastconnected: "",
  status: "",
  intrestedIn: "",
  sex: "",
  age: "",
  mediaLinks: [],
  history: [],
};

const TITLE = { update: "עדכון לקוח", track: "צפייה בלקוח", add: "לקוח חדש" };

// Same checks, order and messages as the old formList validation.
const VALIDATION = [
  { id: "firstname", title: "שם", mendatory: true },
  { id: "phone", validate: (val) => val.length === 10, validateMsg: "טלפון חייב להיות 10 ספרות" },
  { id: "mail", validate: validationEmail, validateMsg: "מייל לא תקין" },
];

// History entry kinds — detected by the prefix the system writes into the text.
const HISTORY_KINDS = [
  { marker: "נשלחה הודעת וואצאפ:##", label: "WhatsApp", color: "#25D366" },
  { marker: "נשלח מייל:##", label: "מייל", color: "#2563EB" },
  { marker: "נשלחה הודעה:##", label: "SMS", color: "#7C3AED" },
  { marker: "נעשתה תזכורת:##", label: "תזכורת", color: "#D97706" },
];
const historyKind = (val) => {
  // Same precedence as before: the last matching rule wins.
  let kind = null;
  HISTORY_KINDS.slice()
    .reverse()
    .forEach((k) => {
      if ((val || "").includes(k.marker)) kind = k;
    });
  return kind;
};

// dd/mm/yyyy hh:mm (same as the old formatEmailDate)
const formatDateTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}`;
};

const CustomerDrawer = (props) => {
  const [open, setOpen] = useState(true);
  const savedRef = useRef(false); // what to report to the parent once the close animation ends
  const [dirty, setDirty] = useState(false);

  const [Sform, setSform] = useState({ ...infoInit });
  const [tagIds, setTagIds] = useState([]); // tag ids, index-aligned with Sform.intrestedIn names
  const [tagDetailsMap, setTagDetailsMap] = useState({}); // tag name → { color, emailSequences, … }

  const [history, setHistory] = useState("");
  const [emailHistory, setEmailHistory] = useState([]);
  const [expandedEmailId, setExpandedEmailId] = useState(null);
  const [loadingEmailHistory, setLoadingEmailHistory] = useState(false);
  const [doneLoadingHistory, setDoneLoadingHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activityTab, setActivityTab] = useState("history"); // "history" | "emails"

  const toDisable = props.flag === "track";

  useEffect(() => {
    componentUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (doneLoadingHistory) {
      setSform((f) => ({ ...f, history: history }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneLoadingHistory]);

  // Every user edit goes through here so the drawer knows it has unsaved changes.
  const edit = (changes) => {
    setDirty(true);
    setSform((f) => ({ ...f, ...changes }));
  };

  const loadTagDetails = async () => {
    try {
      const response = await fetch("/api/tags/stats");
      const tags = await response.json();
      const tagMap = {};
      tags.forEach((tag) => {
        tagMap[tag.name] = {
          id: tag.id,
          name: tag.name,
          color: tag.color || "#4ECDC4",
          emailSequences: tag.emailSequences || [],
          hasActiveSequence: tag.hasActiveSequence || false,
        };
      });
      setTagDetailsMap(tagMap);
    } catch (error) {
      console.error("Error loading tag details:", error);
    }
  };

  const componentUpdate = async () => {
    const { flag, info } = props;

    await loadTagDetails();

    if (flag === "update" || flag === "track") {
      setSform((f) => ({
        ...f,
        id: info.id || "",
        firstname: info.firstname + "" || "",
        lastname: info.lastname || "",
        phone: info.phone || "",
        mail: info.mail || "",
        emailSubscribed: info.emailSubscribed !== undefined ? info.emailSubscribed : true,
        dateEnterSystem: info.dateEnterSystem || "",
        lastconnected: info.lastconnected || "",
        status: info.status || "",
        status_id: info.status_id || null,
        intrestedIn: info.intrestedIn || "",
        tag_ids: info.tag_ids || [],
        sex: info.sex || "",
        age: info.age || "",
        mediaLinks: info.mediaLinks ? JSON.parse(info.mediaLinks) : [],
      }));
      setTagIds(info.tag_ids || []);

      var History_ = await props.getCusHistory(info.id);
      setHistory(History_);
      setDoneLoadingHistory(true);

      setLoadingEmailHistory(true);
      try {
        var emailHistory_ = await props.getCustomerEmailHistory(info.id);
        setEmailHistory(emailHistory_);
      } catch (err) {
        console.error("Error loading email history:", err);
      }
      setLoadingEmailHistory(false);
    }
  };

  // Start the close animation; the parent is told (and unmounts us) in afterClose.
  const handleClose = (saved) => {
    savedRef.current = saved === true;
    setOpen(false);
  };

  const onUpdateHandler = async () => {
    for (var i = 0; i < VALIDATION.length; i++) {
      var item = VALIDATION[i];
      if (item.mendatory && Sform[item.id] === "") {
        props.showAlert(item.title + " חובה והוא ריק");
        return;
      }
      if (Sform[item.id] !== "" && item.validate !== undefined && !item.validate(Sform[item.id])) {
        props.showAlert(item.validateMsg);
        return;
      }
    }
    if (props.flag === "add" && Sform.phone !== "" && props.allPhones[Sform.phone.trim()] === "") {
      props.showAlert(Sform.phone + " כבר קיים במערכת");
      return;
    }
    if (props.flag === "add" && Sform.mail !== "" && props.allEmails[Sform.mail.trim()] === "") {
      props.showAlert(Sform.mail + " כבר קיים במערכת");
      return;
    }
    setSaving(true);
    var res = await props.insertOrUpdateCustomers(Sform);
    setSaving(false);
    if (res === "err") props.showAlert("בעיה בזמן עידכון");
    else {
      props.showAlert("עודכן בהצלחה");
      handleClose(true);
    }
  };

  // ---- History list editing (same semantics as before) ----------------------
  const removeFromList = (index, listName) => {
    var list = [...Sform[listName]];
    list[index].delete = true;
    edit({ [listName]: list });
  };

  const onChangeList = (val, index, listName, isTime) => {
    var list = [...Sform[listName]];
    list[index].update = true;
    if (isTime) list[index].dateTime = val;
    else list[index].val = val.split('"').join("");
    edit({ [listName]: list });
  };

  const addLineToList = (listName) => {
    var list = [...Sform[listName]];
    list.push({ val: "", update: true, dateTime: new Date(), creatorName: props.auth ? props.auth.name : null });
    edit({ [listName]: list });
  };

  // ---- Tags (names in Sform.intrestedIn, ids in tagIds — kept index-aligned) --
  const addTag = (id, opt) => {
    var l = Sform.intrestedIn;
    l = l === "" ? [] : l.split(",");
    l.push(itemLabel(opt.item));
    const newTagIds = [...tagIds, id];
    setTagIds(newTagIds);
    edit({ intrestedIn: l.join(","), tag_ids: newTagIds });
  };

  const removeTag = (i) => {
    var t = Sform.intrestedIn.split(",");
    t.splice(i, 1);
    const newTagIds = [...tagIds];
    newTagIds.splice(i, 1);
    setTagIds(newTagIds);
    edit({ intrestedIn: t.join(","), tag_ids: newTagIds });
  };

  // ---- Render ------------------------------------------------------------------
  const isAdd = props.flag === "add";
  const fullName = [Sform.firstname, Sform.lastname].filter(Boolean).join(" ");
  const title = isAdd ? TITLE.add : fullName || TITLE[props.flag];
  const subtitle = isAdd
    ? null
    : [TITLE[props.flag], Sform.dateEnterSystem ? "נכנס למאגר " + dateToStringForDisplay(Sform.dateEnterSystem) : null]
        .filter(Boolean)
        .join(" · ");

  const currentTags = (Sform.intrestedIn || "")
    .split(",")
    .filter((t) => t.trim())
    .map((name) => {
      const d = tagDetailsMap[name.trim()];
      return d ? d : { name, color: "#4ECDC4" };
    });

  const footer = (requestClose) => (
    <>
      <Button onClick={requestClose}>ביטול</Button>
      {props.flag === "track" ? null : (
        <Button type="primary" loading={saving} onClick={onUpdateHandler}>
          {isAdd ? "הוסף לקוח" : "שמור"}
        </Button>
      )}
    </>
  );

  return (
    <EditDrawer
      open={open}
      title={title}
      subtitle={subtitle}
      dirty={dirty}
      onClose={() => handleClose(false)}
      afterClose={() => props.onClose(savedRef.current)}
      footer={footer}
      extra={props.actions ? <RowActions mode="menu" actions={props.actions} /> : null}
    >
      {/* ---- Details ---- */}
      <Section title="פרטים">
        <FormGrid>
          <Field label="שם" required>
            <Input disabled={toDisable} value={Sform.firstname} onChange={(e) => edit({ firstname: e.target.value })} />
          </Field>
          <Field label="שם משפחה">
            <Input disabled={toDisable} value={Sform.lastname} onChange={(e) => edit({ lastname: e.target.value })} />
          </Field>
          <Field label="טלפון">
            <Input
              disabled={toDisable}
              inputMode="tel"
              value={Sform.phone}
              onChange={(e) => edit({ phone: e.target.value.split("+972").join("0").replace(/\D/g, "") })}
            />
          </Field>
          <Field label="מייל">
            <Input
              disabled={toDisable}
              inputMode="email"
              dir="ltr"
              value={Sform.mail}
              onChange={(e) => edit({ mail: e.target.value })}
            />
          </Field>
          <Field label="סטטוס">
            <Select
              disabled={toDisable}
              showSearch
              allowClear={false}
              optionFilterProp="label"
              placeholder="בחר סטטוס"
              options={props.statusOptions}
              value={Sform.status_id || undefined}
              labelRender={({ value, label }) => (
                <StatusBadge color={statusColor(value)}>{label || Sform.status}</StatusBadge>
              )}
              optionRender={(opt) => <StatusBadge color={statusColor(opt.value)}>{opt.label}</StatusBadge>}
              onChange={(id, opt) => edit({ status: itemLabel(opt.item), status_id: id })}
            />
          </Field>
          <Field label="מין">
            <Input disabled={toDisable} value={Sform.sex} onChange={(e) => edit({ sex: e.target.value })} />
          </Field>
          <Field label="גיל">
            <Input disabled={toDisable} value={Sform.age} onChange={(e) => edit({ age: e.target.value })} />
          </Field>
        </FormGrid>
      </Section>

      {/* ---- Tags ---- */}
      <Section title="טאגים">
        <div className={styles.tagsBox}>
          <TagChips tags={currentTags} onRemove={toDisable ? null : (_tag, i) => removeTag(i)} emptyText="לא נבחרו טאגים" />
        </div>
        {!toDisable ? (
          <Select
            className={styles.addTag}
            showSearch
            optionFilterProp="label"
            placeholder="+ הוספת טאג"
            options={props.tagOptions}
            value={null}
            onChange={addTag}
          />
        ) : null}
      </Section>

      {/* ---- Activity: history | emails (switch) ----
          One section with a toggle, so the email side (mailing settings + sent emails)
          is never buried under a long history. */}
      {renderActivity()}
    </EditDrawer>
  );

  function renderActivity() {
    const historyCount = Array.isArray(Sform.history) ? Sform.history.filter((h) => !h.delete).length : 0;
    const emailCount = emailHistory ? emailHistory.length : 0;
    return (
      <Section
        title={
          <Segmented
            value={activityTab}
            onChange={setActivityTab}
            options={[
              { value: "history", label: `היסטוריה${historyCount ? ` (${historyCount})` : ""}` },
              { value: "emails", label: `אימיילים${emailCount ? ` (${emailCount})` : ""}` },
            ]}
          />
        }
        extra={
          activityTab === "history" && !toDisable ? (
            <Button size="small" icon={<PlusOutlined />} onClick={() => addLineToList("history")}>
              הוסף שורה
            </Button>
          ) : null
        }
      >
        {activityTab === "history" ? renderHistory() : renderEmails()}
      </Section>
    );
  }

  // Email tab: mailing settings (subscription + consent proof) right above the sent emails.
  function renderEmails() {
    return (
      <>
        <div className={styles.mailing}>
          <div className={styles.subscribeRow}>
            <Switch
              disabled={toDisable}
              checked={Sform.emailSubscribed === true}
              onChange={(checked) => edit({ emailSubscribed: checked })}
            />
            <span>{Sform.emailSubscribed ? "אפשר לקבל מיילים" : "חסום למיילים"}</span>
          </div>
          {!isAdd ? <ConsentInfo info={props.info || {}} /> : null}
        </div>
        {!isAdd ? renderEmailHistory() : null}
      </>
    );
  }

  function renderHistory() {
    const list = Sform.history === "" ? null : [...Sform.history];
    // Hide deleted entries, keep their original index (edits address the original array).
    const entries = list
      ? list
          .map((item, originalIndex) => ({ item, originalIndex }))
          .filter(({ item }) => !item.delete)
          .sort((a, b) => {
            if (!a.item.dateTime && b.item.dateTime) return -1;
            if (a.item.dateTime && !b.item.dateTime) return 1;
            if (!a.item.dateTime && !b.item.dateTime) return 0;
            return new Date(a.item.dateTime) - new Date(b.item.dateTime);
          })
          .reverse() // newest first
      : null;

    return (
      <>
        {entries === null || (!doneLoadingHistory && !isAdd) ? (
          <div className={styles.loading}>
            <Spin size="small" />
          </div>
        ) : entries.length === 0 ? (
          <EmptyState compact title="אין עדיין היסטוריה" />
        ) : (
          <ol className={styles.timeline}>
            {entries.map(({ item, originalIndex }) => {
              const kind = historyKind(item.val);
              const text = item.val.split("##").join("\n");
              return (
                <li key={originalIndex} className={styles.entry} style={kind ? { "--kind-color": kind.color } : null}>
                  <span className={styles.dot} />
                  <div className={styles.entryHead}>
                    <span className={`${styles.entryDate} ui-tabular`}>
                      {item.dateTime ? formatDateTime(item.dateTime) : "dd/mm/yyyy hh:mm"}
                    </span>
                    {kind ? <span className={styles.kind}>{kind.label}</span> : null}
                    {item.creatorName ? <span className={styles.creator}>{item.creatorName}</span> : null}
                    {!toDisable ? (
                      <span className={styles.entryTools}>
                        <DatePicker
                          size="small"
                          variant="borderless"
                          showTime={{ format: "HH:mm" }}
                          format="DD/MM/YYYY HH:mm"
                          placeholder="שנה תאריך"
                          allowClear={false}
                          value={null}
                          className={styles.datePicker}
                          onChange={(d) => d && onChangeList(d.format("YYYY-MM-DDTHH:mm"), originalIndex, "history", true)}
                        />
                        <Tooltip title="מחק שורה">
                          <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            className={styles.deleteEntry}
                            onClick={() => removeFromList(originalIndex, "history")}
                          />
                        </Tooltip>
                      </span>
                    ) : null}
                  </div>
                  <Input.TextArea
                    disabled={toDisable}
                    variant="filled"
                    autoSize={{ minRows: 1, maxRows: 6 }}
                    placeholder="הוסף מידע כאן..."
                    value={text}
                    onChange={(e) => onChangeList(e.target.value.split("\n").join("##"), originalIndex, "history")}
                  />
                </li>
              );
            })}
          </ol>
        )}
      </>
    );
  }

  function renderEmailHistory() {
    return (
      <>
        {loadingEmailHistory ? (
          <div className={styles.loading}>
            <Spin size="small" />
          </div>
        ) : !emailHistory || emailHistory.length === 0 ? (
          <EmptyState compact title="לא נשלחו אימיילים ללקוח זה" />
        ) : (
          <ul className={styles.emails}>
            {emailHistory.map((email) => {
              const isExpanded = expandedEmailId === email.id;
              const sent = email.status === "completed";
              const opens = email.opened && email.opened.openCount > 0 ? email.opened.openCount : 0;
              return (
                <li key={email.id} className={styles.email}>
                  <button
                    type="button"
                    className={styles.emailHead}
                    aria-expanded={isExpanded}
                    onClick={() => setExpandedEmailId(isExpanded ? null : email.id)}
                  >
                    <span className={styles.emailSubject}>{email.subject}</span>
                    <span className={styles.emailMeta}>
                      <StatusBadge color={email.type === "manual" ? "#64748B" : "#5B5BD6"}>
                        {email.type === "manual" ? "ידני" : email.sequenceName || "סדרה"}
                      </StatusBadge>
                      <StatusBadge color={sent ? "#16A34A" : "#DC2626"}>{sent ? "נשלח" : "נכשל"}</StatusBadge>
                      <StatusBadge color={opens ? "#0EA5E9" : "#A1A1AA"}>
                        {opens ? "נפתח " + opens + "x" : "לא נפתח"}
                      </StatusBadge>
                      <span className={`${styles.emailDate} ui-tabular`}>{formatDateTime(email.sentAt)}</span>
                      <DownOutlined className={styles.chevron} data-open={isExpanded} />
                    </span>
                  </button>
                  {isExpanded && email.htmlBody ? (
                    <iframe
                      srcDoc={email.htmlBody}
                      className={styles.preview}
                      title="Email Preview"
                      sandbox="allow-same-origin"
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </>
    );
  }
};

// Read-only proof of marketing consent (set by website forms, see utils/marketingConsent.js on the server).
const ConsentInfo = ({ info }) => {
  const value = info.marketingConsent;
  const icon =
    value === true ? (
      <CheckCircleFilled style={{ color: "#16A34A" }} />
    ) : value === false ? (
      <CloseCircleFilled style={{ color: "#A1A1AA" }} />
    ) : (
      <MinusCircleOutlined style={{ color: "#A1A1AA" }} />
    );
  const label = value === true ? "אישר/ה דיוור שיווקי" : value === false ? "לא אישר/ה דיוור שיווקי" : "לא נשאל/ה (לקוח ותיק)";
  const when = info.marketingConsentAt ? new Date(info.marketingConsentAt).toLocaleString("he-IL") : "";
  return (
    <div className={styles.consent} title={info.marketingConsentText || ""}>
      <div className={styles.consentLine}>
        {icon}
        <span>{label}</span>
      </div>
      {when || info.marketingConsentSource ? (
        <div className={styles.consentMeta}>{[info.marketingConsentSource, when].filter(Boolean).join(" · ")}</div>
      ) : null}
      {info.marketingConsentText ? <div className={styles.consentMeta}>״{info.marketingConsentText}״</div> : null}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      showAlert: actionsCreators.showAlert,
      insertOrUpdateCustomers: actionsCreators.insertOrUpdateCustomers,
      setShowSendMassage: actionsCreators.setShowSendMassage,
      getCusHistory: actionsCreators.getCusHistory,
      getCustomerEmailHistory: actionsCreators.getCustomerEmailHistory,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(CustomerDrawer);
